import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import inspectionsService from "../services/inspectionsService";
import styles from "../assets/inspectionEdit.module.css";

const InspectionEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  // State
  const [inspection, setInspection] = useState(null);
  const [property, setProperty] = useState(null);
  const [types, setTypes] = useState([]);
  const [buildingManagers, setBuildingManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [showPropertyTypeahead, setShowPropertyTypeahead] = useState(false);
  const [propertySearchValue, setPropertySearchValue] = useState("");
  const propertyTypeaheadRef = useRef(null);

  // Form data
  const [formData, setFormData] = useState({
    property_id: "",
    property_title: "",
    type_id: "",
    building_manager_id: "",
    day: "",
    time_from: "",
    time_to: "",
    description: "",
  });

  // Load inspection data on component mount
  useEffect(() => {
    loadInspectionData();
  }, []); // Only run once on mount

  // Click outside handler for property typeahead dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showPropertyTypeahead && propertyTypeaheadRef.current && !propertyTypeaheadRef.current.contains(event.target)) {
        setShowPropertyTypeahead(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPropertyTypeahead]);

  // Load inspection data - LIVE DATA ONLY
  const loadInspectionData = async () => {
    // Prevent multiple simultaneous calls
    if (isLoadingData) {
      console.log("Already loading data, skipping...");
      return;
    }

    setIsLoadingData(true);
    setLoading(true);

    try {
      console.log("📡 [FRONTEND] Loading inspection data from live database...");
      const response = await inspectionsService.getInspection(id);
      
      if (!response.success || !response.data) {
        throw new Error(response.message || 'Inspection not found');
      }
      
      const inspectionData = response.data;

      setInspection(inspectionData);
      setProperty(inspectionData.property);

      // Load types and building managers
      const typesResponse = await inspectionsService.getInspectionTypes();
      const buildingManagersResponse = await inspectionsService.getBuildingManagers();

      if (typesResponse && typesResponse.success) {
        setTypes(typesResponse.data || []);
      } else {
        setTypes([]);
      }

      console.log("📋 [FRONTEND] Building managers response:", buildingManagersResponse);
      if (buildingManagersResponse && buildingManagersResponse.success) {
        setBuildingManagers(buildingManagersResponse.data || []);
      } else {
        console.warn("⚠️ [FRONTEND] Building managers not loaded:", buildingManagersResponse);
        setBuildingManagers([]);
      }

      // Set form data
      const propertyName = inspectionData.property?.name || "";

      // Get building manager ID - TypeORM returns relation as buildingManager (camelCase)
      const buildingManagerId = inspectionData.building_manager_id ||
        inspectionData.buildingManager?.id ||
        inspectionData.building_manager?.id;

      // Convert dd/mm/yyyy to yyyy-mm-dd for HTML date input
      const convertDateForInput = (dateStr) => {
        if (!dateStr) return "";
        // If already in yyyy-mm-dd format
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
        // Convert from dd/mm/yyyy to yyyy-mm-dd
        const parts = dateStr.split('/');
        if (parts.length === 3) {
          return `${parts[2]}-${parts[1]}-${parts[0]}`;
        }
        return dateStr;
      };

      console.log("📋 [FRONTEND] Setting form data:", {
        building_manager_id: buildingManagerId,
        day: inspectionData.day,
        time_from: inspectionData.time_from,
        time_to: inspectionData.time_to
      });

      setFormData({
        property_id: inspectionData.property_id || inspectionData.property?.id,
        property_title: propertyName,
        type_id: inspectionData.type_id || inspectionData.type?.id,
        building_manager_id: buildingManagerId || "",
        day: convertDateForInput(inspectionData.day) || "",
        time_from: inspectionData.time_from || "",
        time_to: inspectionData.time_to || "",
        description: inspectionData.description || "",
      });
      setPropertySearchValue(propertyName);
      
      console.log("✅ [FRONTEND] Live database data loaded successfully");
    } catch (error) {
      console.error("❌ [FRONTEND] Error loading inspection data:", error);
      toast.error("Failed to load inspection data. Please check your connection.");
      navigate("/inspections");
    } finally {
      setLoading(false);
      setIsLoadingData(false);
    }
  };


  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  // Handle property search (typeahead)
  const handlePropertySearch = async (value) => {
    setPropertySearchValue(value);
    
    if (value.length > 0) {
      try {
        const response = await inspectionsService.searchProperties(value);
        if (response && response.success) {
          setFilteredProperties(response.data || []);
          setShowPropertyTypeahead(true);
        } else {
          setFilteredProperties([]);
          setShowPropertyTypeahead(false);
        }
      } catch (error) {
        console.error('Error searching properties:', error);
        setFilteredProperties([]);
        setShowPropertyTypeahead(false);
      }
    } else {
      setShowPropertyTypeahead(false);
      setFilteredProperties([]);
    }
  };

  // Handle property selection (typeahead)
  const handlePropertySelect = (selectedProperty) => {
    setFormData((prev) => ({
      ...prev,
      property_id: selectedProperty.id,
      property_title: selectedProperty.name,
    }));
    setProperty(selectedProperty);
    setPropertySearchValue(selectedProperty.name);
    setShowPropertyTypeahead(false);
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.property_id) {
      newErrors.property_id = "Property is required";
    }
    if (!formData.type_id) {
      newErrors.type_id = "Type is required";
    }
    if (!formData.building_manager_id) {
      newErrors.building_manager_id = "Building Manager is required";
    }
    if (!formData.day) {
      newErrors.day = "Day is required";
    }
    if (!formData.time_from) {
      newErrors.time_from = "Time From is required";
    }
    if (!formData.time_to) {
      newErrors.time_to = "Time To is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the errors in the form");
      return;
    }

    try {
      // Convert date from yyyy-mm-dd (HTML input) to dd/mm/yyyy (Laravel format)
      const convertDateForBackend = (dateStr) => {
        if (!dateStr) return "";
        // If already in dd/mm/yyyy format, return as is
        if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) return dateStr;
        // Convert from yyyy-mm-dd to dd/mm/yyyy
        const parts = dateStr.split('-');
        if (parts.length === 3) {
          return `${parts[2]}/${parts[1]}/${parts[0]}`;
        }
        return dateStr;
      };

      const submitData = {
        ...formData,
        day: convertDateForBackend(formData.day),
      };

      await inspectionsService.updateInspection(id, submitData);
      toast.success("Inspection updated successfully!");
      navigate("/inspections");
    } catch (error) {
      console.error("Error updating inspection:", error);
      toast.error(error.response?.data?.message || "Failed to update inspection");
    }
  };

  // Handle cancel
  const handleCancel = () => {
    navigate("/inspections");
  };

  // Show loading state while fetching data
  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>Loading inspection...</p>
        </div>
      </div>
    );
  }

  if (!inspection) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          <p>Inspection not found</p>
          <button onClick={() => navigate("/inspections")}>
            Back to Inspections
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Breadcrumbs */}
      <div className={styles.breadcrumbs}>
        <Link to="/dashboard">Dashboard</Link>
        <span className={styles.separator}>/</span>
        <Link to="/inspections">Inspections</Link>
        <span className={styles.separator}>/</span>
        <span className={styles.currentPage}>Edit Inspection</span>
      </div>


      <div className={styles.row}>
        <div className={styles.colMd12}>
          <div className={styles.panel}>
            <div className={styles.panelHeading}>
              <h3>Edit Inspection</h3>
            </div>
            <div className={styles.panelBody}>
              <form onSubmit={handleSubmit}>
                <fieldset className={styles.formHorizontal}>
                  {/* Property Field */}
                  <div className={styles.formGroup}>
                    <label
                      className={`${styles.controlLabel} ${styles.colSm3} ${styles.labelRequired}`}
                    >
                      Property
                    </label>
                    <div className={styles.colSm5} ref={propertyTypeaheadRef} style={{ position: 'relative' }}>
                      <input
                        type="text"
                        className={`${styles.formControl} ${styles.jsPropertyFieldTypeahead}`}
                        id="property_title"
                        name="property_title"
                        value={propertySearchValue}
                        onChange={(e) => handlePropertySearch(e.target.value)}
                        required
                        placeholder="Type property ID or name (or *)"
                      />
                      <input
                        type="hidden"
                        className={styles.jsPropertyIdField}
                        name="property_id"
                        value={formData.property_id}
                      />
                      {showPropertyTypeahead && filteredProperties.length > 0 && (
                        <div className={styles.typeaheadDropdown} style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          backgroundColor: 'white',
                          border: '1px solid #ccc',
                          borderTop: 'none',
                          maxHeight: '200px',
                          overflowY: 'auto',
                          zIndex: 1000,
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}>
                          {filteredProperties.map((property) => (
                            <div
                              key={property.id}
                              onClick={() => handlePropertySelect(property)}
                              style={{
                                padding: '8px 12px',
                                cursor: 'pointer',
                                borderBottom: '1px solid #eee'
                              }}
                              onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                              onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                            >
                              <strong>{property.name} {property.property_id ? `(${property.property_id})` : ''}</strong>
                            </div>
                          ))}
                        </div>
                      )}
                      {errors.property_id && (
                        <div className={styles.errorMessage}>
                          {errors.property_id}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Type Field */}
                  <div className={styles.formGroup}>
                    <label
                      className={`${styles.controlLabel} ${styles.colSm3} ${styles.labelRequired}`}
                    >
                      Type
                    </label>
                    <div className={styles.colSm5}>
                      <select
                        name="type_id"
                        value={formData.type_id}
                        onChange={handleInputChange}
                        className={styles.formControl}
                        required
                      >
                        <option value="">Please select</option>
                        {types.map((type) => (
                          <option key={type.id} value={type.id}>
                            {type.name}
                          </option>
                        ))}
                      </select>
                      {errors.type_id && (
                        <div className={styles.errorMessage}>
                          {errors.type_id}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Building Manager Field */}
                  <div className={styles.formGroup}>
                    <label
                      className={`${styles.controlLabel} ${styles.colSm3} ${styles.labelRequired}`}
                    >
                      Building Manager
                    </label>
                    <div className={styles.colSm5}>
                      <select
                        name="building_manager_id"
                        value={formData.building_manager_id}
                        onChange={handleInputChange}
                        className={styles.formControl}
                        required
                      >
                        <option value="">Please select</option>
                        {buildingManagers.map((manager) => (
                          <option key={manager.id} value={manager.id}>
                            {manager.full_name || manager.name}
                          </option>
                        ))}
                      </select>
                      {errors.building_manager_id && (
                        <div className={styles.errorMessage}>
                          {errors.building_manager_id}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Day Field */}
                  <div className={styles.formGroup}>
                    <label
                      className={`${styles.controlLabel} ${styles.colSm3} ${styles.labelRequired}`}
                    >
                      Day
                    </label>
                    <div className={styles.colSm5}>
                      <input
                        type="date"
                        name="day"
                        value={formData.day}
                        onChange={handleInputChange}
                        className={styles.formControl}
                        required
                      />
                      {errors.day && (
                        <div className={styles.errorMessage}>{errors.day}</div>
                      )}
                    </div>
                  </div>

                  {/* Time From Field */}
                  <div className={styles.formGroup}>
                    <label
                      className={`${styles.controlLabel} ${styles.colSm3} ${styles.labelRequired}`}
                    >
                      Time From
                    </label>
                    <div className={styles.colSm5}>
                      <input
                        type="time"
                        name="time_from"
                        value={formData.time_from}
                        onChange={handleInputChange}
                        className={styles.formControl}
                        required
                      />
                      {errors.time_from && (
                        <div className={styles.errorMessage}>
                          {errors.time_from}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Time To Field */}
                  <div className={styles.formGroup}>
                    <label
                      className={`${styles.controlLabel} ${styles.colSm3} ${styles.labelRequired}`}
                    >
                      Time To
                    </label>
                    <div className={styles.colSm5}>
                      <input
                        type="time"
                        name="time_to"
                        value={formData.time_to}
                        onChange={handleInputChange}
                        className={styles.formControl}
                        required
                      />
                      {errors.time_to && (
                        <div className={styles.errorMessage}>
                          {errors.time_to}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Description Field */}
                  <div className={styles.formGroup}>
                    <label
                      className={`${styles.controlLabel} ${styles.colSm3}`}
                    >
                      Description
                    </label>
                    <div className={styles.colSm5}>
                      <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        className={styles.formControl}
                        rows="4"
                        placeholder="Enter description..."
                      />
                      {errors.description && (
                        <div className={styles.errorMessage}>
                          {errors.description}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div
                    className={styles.formGroup}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                    }}
                  >
                    <div className={styles.colSm3}></div>
                    <div className={styles.colSm5}>
                      <button
                        style={{ backgroundColor: "#1B2046" }}
                        type="submit"
                        className={styles.btnHead}
                      >
                        <i className="glyphicon glyphicon-ok"></i>
                        Save
                      </button>
                    </div>
                  </div>
                </fieldset>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InspectionEdit;
